import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom/client';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Modal,
  Form,
  Select,
  DatePicker,
  Typography,
  Row,
  Col,
  Statistic,
  Descriptions,
  Tabs,
  Dropdown,
  MenuProps,
  Tooltip,
  Popconfirm,
  message,
  App,
  Spin,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserOutlined,
  SearchOutlined,
  MoreOutlined,
  DownloadOutlined,
  DeleteOutlined,
  PlusOutlined,
  PrinterOutlined,
  SaveOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  useIIRs,
  useIIR,
  useApproveIIR,
  useRejectIIR,
  useAssignInspector,
  useUpdateIIRInspectionDetails,
} from '../../hooks/useQCWorkflow';
import { InitialInspectionRequest } from '../../services/qc-workflow.service';
import { useWorkOrder } from '../../hooks/useWorkOrder';
import { useUsers } from '../../hooks/useUsers';
import { useAuthStore } from '../../store/authStore';
import { DocumentList, DocumentUpload } from '../../components/documents';
import { API_BASE_URL } from '@/config/apiBaseUrl';
import { buildUserIdToLabelMap, resolveUserDisplayFromMap } from '@/utils/userDisplay';
import { PrintableInitialInspectionReport } from '@/components/quality/PrintableInitialInspectionReport';

const { Title } = Typography;
const { Search } = Input;
const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Option } = Select;

const InitialInspectionRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: routeIIRId } = useParams<{ id?: string }>();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedIIR, setSelectedIIR] = useState<InitialInspectionRequest | null>(null);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
  const [isAssignModalVisible, setIsAssignModalVisible] = useState(false);
  const [reportForm] = Form.useForm();
  const [rejectForm] = Form.useForm();
  const [assignForm] = Form.useForm();

  // Fetch all IIRs with filters
  const filters = useMemo(() => {
    const filterObj: { status?: string; workOrderId?: string } = {};
    if (statusFilter) filterObj.status = statusFilter;
    return filterObj;
  }, [statusFilter]);

  const { data: allIIRs = [], isLoading, refetch } = useIIRs(filters);
  const { data: routeIIR, isLoading: isRouteIIRLoading } = useIIR(routeIIRId || '', !!routeIIRId);
  const approveMutation = useApproveIIR();
  const rejectMutation = useRejectIIR();
  const assignMutation = useAssignInspector();
  const updateInspectionDetailsMutation = useUpdateIIRInspectionDetails();
  const { user } = useAuthStore();
  const { message: messageApi } = App.useApp();

  // Get work order details for selected IIR (with documents relation)
  const { data: workOrder, refetch: refetchWorkOrder } = useWorkOrder(selectedIIR?.work_order_id || '');
  
  // Debug: Log work order documents
  React.useEffect(() => {
    if (workOrder && selectedIIR?.work_order_id) {
      console.log('[IIR Page] Work Order:', workOrder);
      console.log('[IIR Page] Work Order Documents (from relation):', workOrder.documents);
      console.log('[IIR Page] Work Order ID:', selectedIIR.work_order_id);
    }
  }, [workOrder, selectedIIR?.work_order_id]);

  React.useEffect(() => {
    if (routeIIRId && routeIIR) {
      setSelectedIIR(routeIIR);
      setReportFormValues(routeIIR);
    }
  }, [routeIIRId, routeIIR]);

  // Get users for inspector assignment (only active users)
  const { data: users = [], isLoading: isLoadingUsers } = useUsers({
    company_id: user?.company_id,
    is_active: true,
  });

  const userDisplayMap = useMemo(() => buildUserIdToLabelMap(users), [users]);
  const displayUser = (id?: string | null) => resolveUserDisplayFromMap(userDisplayMap, id, '—');

  // Filter IIRs based on search, date range, and active tab
  const filteredIIRs = useMemo(() => {
    let filtered = allIIRs;

    // Filter by active tab
    if (activeTab !== 'all') {
      filtered = filtered.filter(iir => {
        if (activeTab === 'pending') return iir.status === 'Pending';
        if (activeTab === 'in-progress') return iir.status === 'In Progress';
        if (activeTab === 'approved') return iir.status === 'Approved';
        if (activeTab === 'rejected') return iir.status === 'Rejected';
        return true;
      });
    }

    // Filter by search text
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(iir =>
        iir.iir_number.toLowerCase().includes(searchLower) ||
        iir.workOrder?.work_order_number?.toLowerCase().includes(searchLower) ||
        iir.workOrder?.item_name?.toLowerCase().includes(searchLower)
      );
    }

    // Filter by date range
    if (dateRange && dateRange[0] && dateRange[1]) {
      filtered = filtered.filter(iir => {
        const requestedDate = dayjs(iir.requested_date);
        return requestedDate.isAfter(dateRange[0].subtract(1, 'day')) &&
               requestedDate.isBefore(dateRange[1].add(1, 'day'));
      });
    }

    return filtered;
  }, [allIIRs, activeTab, searchText, dateRange]);

  // Calculate statistics
  const stats = useMemo(() => {
    return {
      total: allIIRs.length,
      pending: allIIRs.filter(iir => iir.status === 'Pending').length,
      inProgress: allIIRs.filter(iir => iir.status === 'In Progress').length,
      approved: allIIRs.filter(iir => iir.status === 'Approved').length,
      rejected: allIIRs.filter(iir => iir.status === 'Rejected').length,
    };
  }, [allIIRs]);

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      Pending: 'default',
      'In Progress': 'processing',
      Approved: 'success',
      Rejected: 'error',
      Cancelled: 'default',
    };
    return colors[status] || 'default';
  };

  const toEditableRows = (rows: any[] | null | undefined, emptyFactory: () => any) => {
    if (!Array.isArray(rows) || rows.length === 0) {
      return [emptyFactory()];
    }
    return rows.map((row) => ({
      s_no: row?.s_no || '',
      finding: row?.finding || '',
      action_to_be_taken: row?.action_to_be_taken || '',
      part: row?.part || '',
      qty: row?.qty || '',
      uom: row?.uom || '',
    }));
  };

  const setReportFormValues = (iir: InitialInspectionRequest) => {
    reportForm.setFieldsValue({
      report_title: iir.report_title || 'Initial Job Inspection Report',
      report_subtitle: iir.report_subtitle || '',
      report_no: iir.report_no || iir.iir_number,
      customer_name:
        iir.customer_name ||
        iir.workOrder?.salesOrder?.customer_name ||
        iir.workOrder?.customer_name ||
        '',
      report_date: iir.report_date ? dayjs(iir.report_date) : (iir.requested_date ? dayjs(iir.requested_date) : null),
      job_no: iir.job_no || iir.workOrder?.work_order_number || '',
      wr_no: iir.wr_no || iir.workOrder?.work_order_number || '',
      client_ref_1: iir.client_ref_1 || '',
      client_ref_2: iir.client_ref_2 || '',
      po_no: iir.po_no || '',
      make: iir.make || '',
      gate_pass_no: iir.gate_pass_no || '',
      qty_no:
        iir.qty_no ||
        (iir.workOrder?.planned_quantity !== undefined && iir.workOrder?.planned_quantity !== null
          ? `${iir.workOrder?.planned_quantity}${iir.workOrder?.uom ? ` ${iir.workOrder?.uom}` : ''}`.trim()
          : ''),
      work_description: iir.work_description || iir.workOrder?.client_scope_of_work || '',
      equipment_name: iir.equipment_name || iir.workOrder?.item_name || '',
      scope_of_work_text:
        iir.scope_of_work_text ||
        iir.workOrder?.client_scope_of_work ||
        (Array.isArray(iir.workOrder?.work_scope) ? iir.workOrder?.work_scope.filter(Boolean).join(', ') : '') ||
        '',
      remarks: iir.remarks || '',
      findings: iir.findings || '',
      observations: iir.observations || '',
      findings_rows: toEditableRows(iir.findings_rows, () => ({ s_no: '', finding: '', action_to_be_taken: '' })),
      parts_replace_rows: toEditableRows(iir.parts_replace_rows, () => ({ s_no: '', part: '', qty: '', uom: '' })),
      parts_repair_rows: toEditableRows(iir.parts_repair_rows, () => ({ s_no: '', part: '', qty: '', uom: '' })),
      prepared_by_name: iir.prepared_by_name || '',
      prepared_by_print_name: iir.prepared_by_print_name || '',
      prepared_by_designation: iir.prepared_by_designation || '',
      approved_by_name: iir.approved_by_name || '',
      approved_by_print_name: iir.approved_by_print_name || '',
      approved_by_designation: iir.approved_by_designation || '',
    });
  };

  const handleView = (iir: InitialInspectionRequest) => {
    navigate(`/qms/initial-inspection-requests/${iir.id}`);
  };

  const handleApprove = async (iir: InitialInspectionRequest) => {
    try {
      await approveMutation.mutateAsync(iir.id);
      refetch();
    } catch (error) {
      console.error('Error approving IIR:', error);
    }
  };

  const handleReject = (iir: InitialInspectionRequest) => {
    setSelectedIIR(iir);
    rejectForm.resetFields();
    setIsRejectModalVisible(true);
  };

  const handleRejectSubmit = async (values: { rejectionReason: string }) => {
    if (!selectedIIR) return;
    try {
      await rejectMutation.mutateAsync({
        id: selectedIIR.id,
        rejectionReason: values.rejectionReason,
      });
      setIsRejectModalVisible(false);
      rejectForm.resetFields();
      refetch();
    } catch (error) {
      console.error('Error rejecting IIR:', error);
    }
  };

  const handleAssignInspector = (iir: InitialInspectionRequest) => {
    setSelectedIIR(iir);
    assignForm.resetFields();
    setIsAssignModalVisible(true);
  };

  const handleAssignSubmit = async (values: { inspectorId: string }) => {
    if (!selectedIIR) return;
    try {
      await assignMutation.mutateAsync({ id: selectedIIR.id, inspectorId: values.inspectorId });
      setIsAssignModalVisible(false);
      assignForm.resetFields();
      refetch();
    } catch (error: any) {
      console.error('Error assigning inspector:', error);
    }
  };

  const compactRows = (rows: any[] | undefined, keys: string[]) => {
    if (!Array.isArray(rows)) return null;
    const filtered = rows
      .map((row) => {
        const out: Record<string, string> = {};
        keys.forEach((key) => {
          out[key] = String(row?.[key] ?? '').trim();
        });
        return out;
      })
      .filter((row) => keys.some((key) => row[key]));
    return filtered.length > 0 ? filtered : null;
  };

  const handleSaveReport = async () => {
    if (!selectedIIR) return;
    try {
      const values = await reportForm.validateFields();
      const payload = {
        report_title: values.report_title || null,
        report_subtitle: values.report_subtitle || null,
        report_no: values.report_no || null,
        customer_name: values.customer_name || null,
        report_date: values.report_date ? dayjs(values.report_date).format('YYYY-MM-DD') : null,
        job_no: values.job_no || null,
        wr_no: values.wr_no || null,
        client_ref_1: values.client_ref_1 || null,
        client_ref_2: values.client_ref_2 || null,
        po_no: values.po_no || null,
        make: values.make || null,
        gate_pass_no: values.gate_pass_no || null,
        qty_no: values.qty_no || null,
        work_description: values.work_description || null,
        equipment_name: values.equipment_name || null,
        scope_of_work_text: values.scope_of_work_text || null,
        remarks: values.remarks || null,
        findings: values.findings || null,
        observations: values.observations || null,
        findings_rows: compactRows(values.findings_rows, ['s_no', 'finding', 'action_to_be_taken']),
        parts_replace_rows: compactRows(values.parts_replace_rows, ['s_no', 'part', 'qty', 'uom']),
        parts_repair_rows: compactRows(values.parts_repair_rows, ['s_no', 'part', 'qty', 'uom']),
        prepared_by_name: values.prepared_by_name || null,
        prepared_by_print_name: values.prepared_by_print_name || null,
        prepared_by_designation: values.prepared_by_designation || null,
        approved_by_name: values.approved_by_name || null,
        approved_by_print_name: values.approved_by_print_name || null,
        approved_by_designation: values.approved_by_designation || null,
      };
      const updated = await updateInspectionDetailsMutation.mutateAsync({
        id: selectedIIR.id,
        data: payload,
      });
      setSelectedIIR(updated);
      setReportFormValues(updated);
      refetch();
    } catch (error) {
      console.error('Error updating IIR report details:', error);
    }
  };

  const handlePrintReport = () => {
    if (!selectedIIR) return;
    const values = reportForm.getFieldsValue();
    const printIIR: InitialInspectionRequest = {
      ...selectedIIR,
      report_title: values.report_title ?? selectedIIR.report_title,
      report_subtitle: values.report_subtitle ?? selectedIIR.report_subtitle,
      report_no: values.report_no ?? selectedIIR.report_no,
      customer_name: values.customer_name ?? selectedIIR.customer_name,
      report_date: values.report_date
        ? dayjs(values.report_date).format('YYYY-MM-DD')
        : (selectedIIR.report_date ?? null),
      job_no: values.job_no ?? selectedIIR.job_no,
      wr_no: values.wr_no ?? selectedIIR.wr_no,
      client_ref_1: values.client_ref_1 ?? selectedIIR.client_ref_1,
      client_ref_2: values.client_ref_2 ?? selectedIIR.client_ref_2,
      po_no: values.po_no ?? selectedIIR.po_no,
      make: values.make ?? selectedIIR.make,
      gate_pass_no: values.gate_pass_no ?? selectedIIR.gate_pass_no,
      qty_no: values.qty_no ?? selectedIIR.qty_no,
      work_description: values.work_description ?? selectedIIR.work_description,
      equipment_name: values.equipment_name ?? selectedIIR.equipment_name,
      scope_of_work_text: values.scope_of_work_text ?? selectedIIR.scope_of_work_text,
      remarks: values.remarks ?? selectedIIR.remarks,
      findings_rows: compactRows(values.findings_rows, ['s_no', 'finding', 'action_to_be_taken']) as any,
      parts_replace_rows: compactRows(values.parts_replace_rows, ['s_no', 'part', 'qty', 'uom']) as any,
      parts_repair_rows: compactRows(values.parts_repair_rows, ['s_no', 'part', 'qty', 'uom']) as any,
      prepared_by_name: values.prepared_by_name ?? selectedIIR.prepared_by_name,
      prepared_by_print_name: values.prepared_by_print_name ?? selectedIIR.prepared_by_print_name,
      prepared_by_designation: values.prepared_by_designation ?? selectedIIR.prepared_by_designation,
      approved_by_name: values.approved_by_name ?? selectedIIR.approved_by_name,
      approved_by_print_name: values.approved_by_print_name ?? selectedIIR.approved_by_print_name,
      approved_by_designation: values.approved_by_designation ?? selectedIIR.approved_by_designation,
    };

    const mainContent = document.getElementById('root');
    if (mainContent) mainContent.style.display = 'none';
    const printContainer = document.createElement('div');
    printContainer.id = 'iir-print-container';
    printContainer.style.display = 'block';
    document.body.appendChild(printContainer);
    const root = ReactDOM.createRoot(printContainer);
    const cleanup = () => {
      root.unmount();
      if (document.body.contains(printContainer)) document.body.removeChild(printContainer);
      if (mainContent) mainContent.style.display = 'block';
      window.removeEventListener('afterprint', cleanup);
    };
    root.render(
      <PrintableInitialInspectionReport
        iir={printIIR}
        onClose={cleanup}
      />
    );
    window.addEventListener('afterprint', cleanup);
  };

  const handleWorkOrderClick = (workOrderId: string) => {
            navigate(`/mfg/work-orders/${workOrderId}`);
  };

  const columns: ColumnsType<InitialInspectionRequest> = [
    {
      title: 'IIR Number',
      dataIndex: 'iir_number',
      key: 'iir_number',
      width: 150,
      fixed: 'left',
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => handleView(record)}
          style={{ padding: 0 }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: 'Work Order',
      key: 'work_order',
      width: 150,
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => handleWorkOrderClick(record.work_order_id)}
          style={{ padding: 0 }}
        >
          {record.workOrder?.work_order_number || record.work_order_id.substring(0, 8)}
        </Button>
      ),
    },
    {
      title: 'Item/Description',
      key: 'item_description',
      width: 250,
      render: (_, record) => (
        <span>{record.workOrder?.item_name || '-'}</span>
      ),
    },
    {
      title: 'Requested Date',
      dataIndex: 'requested_date',
      key: 'requested_date',
      width: 130,
      render: (date: string) => date ? dayjs(date).format('DD/MM/YYYY') : '-',
      sorter: (a, b) => dayjs(a.requested_date).unix() - dayjs(b.requested_date).unix(),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => <Tag color={getStatusColor(status)}>{status}</Tag>,
      filters: [
        { text: 'Pending', value: 'Pending' },
        { text: 'In Progress', value: 'In Progress' },
        { text: 'Approved', value: 'Approved' },
        { text: 'Rejected', value: 'Rejected' },
        { text: 'Cancelled', value: 'Cancelled' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Inspector',
      key: 'inspector',
      width: 150,
      render: (_, record) => (
        <span>{displayUser(record.inspector_id)}</span>
      ),
    },
    {
      title: 'Inspection Date',
      dataIndex: 'inspection_date',
      key: 'inspection_date',
      width: 130,
      render: (date: string) => date ? dayjs(date).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => {
        const menuItems: MenuProps['items'] = [
          {
            key: 'view',
            label: 'View Details',
            icon: <EyeOutlined />,
            onClick: () => handleView(record),
          },
        ];

        if (record.status === 'Pending') {
          menuItems.push({
            key: 'assign',
            label: 'Assign Inspector',
            icon: <UserOutlined />,
            onClick: () => handleAssignInspector(record),
          });
        }

        if (record.status === 'In Progress') {
          menuItems.push(
            {
              key: 'approve',
              label: 'Approve',
              icon: <CheckCircleOutlined />,
              onClick: () => handleApprove(record),
            },
            {
              key: 'reject',
              label: 'Reject',
              icon: <CloseCircleOutlined />,
              danger: true,
              onClick: () => handleReject(record),
            }
          );
        }

        return (
          <Dropdown menu={{ items: menuItems }} trigger={['click']}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  const tabItems = [
    { key: 'all', label: `All (${allIIRs.length})` },
    { key: 'pending', label: `Pending (${stats.pending})` },
    { key: 'in-progress', label: `In Progress (${stats.inProgress})` },
    { key: 'approved', label: `Approved (${stats.approved})` },
    { key: 'rejected', label: `Rejected (${stats.rejected})` },
  ];

  if (routeIIRId) {
    if (isRouteIIRLoading && !selectedIIR) {
      return (
        <div style={{ padding: 24 }}>
          <Spin />
        </div>
      );
    }

    if (!selectedIIR) {
      return (
        <div style={{ padding: 24 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/qms/initial-inspection-requests')}>
            Back to Initial Inspection Requests
          </Button>
        </div>
      );
    }

    return (
      <div style={{ padding: 24 }}>
        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }} wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/qms/initial-inspection-requests')}>
            Back to Initial Inspection Requests
          </Button>
          <Space wrap>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSaveReport}
              loading={updateInspectionDetailsMutation.isPending}
            >
              Save Report
            </Button>
            <Button icon={<PrinterOutlined />} onClick={handlePrintReport}>
              Print
            </Button>
            {selectedIIR.status === 'Pending' && (
              <Button icon={<UserOutlined />} onClick={() => handleAssignInspector(selectedIIR)}>
                Assign Inspector
              </Button>
            )}
            {selectedIIR.status === 'In Progress' && (
              <>
                <Button danger icon={<CloseCircleOutlined />} onClick={() => handleReject(selectedIIR)}>
                  Reject
                </Button>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleApprove(selectedIIR)}
                  loading={approveMutation.isPending}
                >
                  Approve
                </Button>
              </>
            )}
          </Space>
        </Space>

        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Descriptions column={2} bordered>
            <Descriptions.Item label="IIR Number">{selectedIIR.iir_number}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={getStatusColor(selectedIIR.status)}>{selectedIIR.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Work Order">
              {workOrder?.work_order_number || selectedIIR.work_order_id}
            </Descriptions.Item>
            <Descriptions.Item label="Item">
              {workOrder?.item_name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Requested Date">
              {dayjs(selectedIIR.requested_date).format('DD/MM/YYYY')}
            </Descriptions.Item>
            <Descriptions.Item label="Requested By">
              {selectedIIR.requester?.name || displayUser(selectedIIR.requested_by)}
            </Descriptions.Item>
          </Descriptions>

          <Card title="Initial Inspection Report">
            <Form form={reportForm} layout="vertical">
              <Row gutter={12}>
                <Col xs={24} md={12}>
                  <Form.Item name="report_title" label="Report Title">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="report_subtitle" label="Report Subtitle">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col xs={24} md={8}>
                  <Form.Item name="customer_name" label="Customer">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="report_no" label="Report No">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="job_no" label="Job No">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col xs={24} md={8}>
                  <Form.Item name="wr_no" label="WR No">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="report_date" label="Date">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="equipment_name" label="Equipment Name">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col xs={24} md={8}>
                  <Form.Item name="qty_no" label="Qty No">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="work_description" label="Work Description">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="scope_of_work_text" label="Scope of Work">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        </Space>

        <Modal
          title="Reject IIR"
          open={isRejectModalVisible}
          onOk={() => rejectForm.submit()}
          onCancel={() => {
            setIsRejectModalVisible(false);
            rejectForm.resetFields();
          }}
          confirmLoading={rejectMutation.isPending}
        >
          <Form form={rejectForm} layout="vertical" onFinish={handleRejectSubmit}>
            <Form.Item
              name="rejectionReason"
              label="Rejection Reason"
              rules={[{ required: true, message: 'Please enter rejection reason' }]}
            >
              <TextArea rows={4} placeholder="Enter reason for rejection" />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="Assign Inspector"
          open={isAssignModalVisible}
          onOk={() => assignForm.submit()}
          onCancel={() => {
            setIsAssignModalVisible(false);
            assignForm.resetFields();
          }}
          okText="Assign"
          confirmLoading={assignMutation.isPending}
        >
          <Form form={assignForm} layout="vertical" onFinish={handleAssignSubmit}>
            <Form.Item
              name="inspectorId"
              label="Inspector"
              rules={[{ required: true, message: 'Please select an inspector' }]}
            >
              <Select
                showSearch
                placeholder="Select inspector"
                loading={isLoadingUsers}
                optionFilterProp="children"
              >
                {users.map((u) => (
                  <Option key={u.id} value={u.id}>
                    {`${u.first_name} ${u.last_name || ''}`.trim() || u.email}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Initial Inspection Requests</Title>

      {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={5}>
          <Card>
            <Statistic title="Total" value={stats.total} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={5}>
          <Card>
            <Statistic title="Pending" value={stats.pending} valueStyle={{ color: '#8c8c8c' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={5}>
          <Card>
            <Statistic title="In Progress" value={stats.inProgress} valueStyle={{ color: '#145A54' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={5}>
          <Card>
            <Statistic title="Approved" value={stats.approved} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic title="Rejected" value={stats.rejected} valueStyle={{ color: '#ff4d4f' }} />
          </Card>
        </Col>
      </Row>

      <Card>
        {/* Filters */}
        <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }} size="middle">
          <Space wrap>
            <Search
              placeholder="Search by IIR number, work order..."
              allowClear
              style={{ width: 300 }}
              onSearch={setSearchText}
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchOutlined />}
            />
            <Select
              placeholder="Filter by status"
              allowClear
              style={{ width: 200 }}
              value={statusFilter || undefined}
              onChange={setStatusFilter}
            >
              <Option value="Pending">Pending</Option>
              <Option value="In Progress">In Progress</Option>
              <Option value="Approved">Approved</Option>
              <Option value="Rejected">Rejected</Option>
              <Option value="Cancelled">Cancelled</Option>
            </Select>
            <RangePicker
              placeholder={['Start Date', 'End Date']}
              onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
              value={dateRange}
            />
          </Space>

          {/* Tabs */}
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
          />
        </Space>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={filteredIIRs}
          loading={isLoading}
          rowKey="id"
          scroll={{ x: 1400 }}
          pagination={{ defaultPageSize: 10, showSizeChanger: true }}
        />
      </Card>

      {/* IIR Detail Modal */}
      <Modal
        title={`IIR Details - ${selectedIIR?.iir_number}`}
        open={isDetailModalVisible}
        onCancel={() => {
          setIsDetailModalVisible(false);
          setSelectedIIR(null);
          reportForm.resetFields();
        }}
        footer={[
          <Button key="close" onClick={() => {
            setIsDetailModalVisible(false);
            setSelectedIIR(null);
            reportForm.resetFields();
          }}>
            Close
          </Button>,
          <Button
            key="saveReport"
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSaveReport}
            loading={updateInspectionDetailsMutation.isPending}
          >
            Save Report
          </Button>,
          <Button key="printReport" icon={<PrinterOutlined />} onClick={handlePrintReport}>
            Print
          </Button>,
          selectedIIR?.status === 'Pending' && (
            <Button
              key="assign"
              type="default"
              icon={<UserOutlined />}
              onClick={() => {
                setIsDetailModalVisible(false);
                handleAssignInspector(selectedIIR);
              }}
            >
              Assign Inspector
            </Button>
          ),
          selectedIIR?.status === 'In Progress' && (
            <Button
              key="reject"
              danger
              icon={<CloseCircleOutlined />}
              onClick={() => {
                setIsDetailModalVisible(false);
                handleReject(selectedIIR);
              }}
            >
              Reject
            </Button>
          ),
          selectedIIR?.status === 'In Progress' && (
            <Button
              key="approve"
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => handleApprove(selectedIIR)}
              loading={approveMutation.isPending}
            >
              Approve
            </Button>
          ),
          selectedIIR?.work_order_id && (
            <Button
              key="workOrder"
              onClick={() => handleWorkOrderClick(selectedIIR.work_order_id)}
            >
              View Work Order
            </Button>
          ),
        ]}
        width={1000}
      >
        {selectedIIR && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Descriptions column={2} bordered>
              <Descriptions.Item label="IIR Number">{selectedIIR.iir_number}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(selectedIIR.status)}>{selectedIIR.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Work Order">
                {workOrder?.work_order_number || selectedIIR.work_order_id}
              </Descriptions.Item>
              <Descriptions.Item label="Item">
                {workOrder?.item_name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Requested Date">
                {dayjs(selectedIIR.requested_date).format('DD/MM/YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="Requested By">
                {selectedIIR.requester?.name || displayUser(selectedIIR.requested_by)}
              </Descriptions.Item>
              {selectedIIR.inspector_id && (
                <>
                  <Descriptions.Item label="Inspector">
                    {displayUser(selectedIIR.inspector_id)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Inspection Date">
                    {selectedIIR.inspection_date ? dayjs(selectedIIR.inspection_date).format('DD/MM/YYYY') : '-'}
                  </Descriptions.Item>
                </>
              )}
              {selectedIIR.approved_date && (
                <>
                  <Descriptions.Item label="Approved Date">
                    {dayjs(selectedIIR.approved_date).format('DD/MM/YYYY')}
                  </Descriptions.Item>
                  <Descriptions.Item label="Approved By">
                    {selectedIIR.approver?.name || displayUser(selectedIIR.approved_by)}
                  </Descriptions.Item>
                </>
              )}
              {selectedIIR.rejection_reason && (
                <Descriptions.Item label="Rejection Reason" span={2}>
                  {selectedIIR.rejection_reason}
                </Descriptions.Item>
              )}
              {selectedIIR.findings && (
                <Descriptions.Item label="Findings" span={2}>
                  {selectedIIR.findings}
                </Descriptions.Item>
              )}
              {selectedIIR.observations && (
                <Descriptions.Item label="Observations" span={2}>
                  {selectedIIR.observations}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Card title="Initial Inspection Report">
              <Form form={reportForm} layout="vertical">
                <Row gutter={12}>
                  <Col xs={24} md={12}>
                    <Form.Item name="report_title" label="Report Title">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="report_subtitle" label="Report Subtitle">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col xs={24} md={8}>
                    <Form.Item name="customer_name" label="Customer">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="report_no" label="Report No">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="job_no" label="Job No">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col xs={24} md={8}>
                    <Form.Item name="wr_no" label="WR No">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="report_date" label="Date">
                      <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="equipment_name" label="Equipment Name">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col xs={24} md={8}>
                    <Form.Item name="client_ref_1" label="Client Ref 1">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="client_ref_2" label="Client Ref 2">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="po_no" label="PO No">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col xs={24} md={8}>
                    <Form.Item name="make" label="Make">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="gate_pass_no" label="Gate Pass No">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="qty_no" label="Qty No">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name="work_description" label="Work Description">
                  <TextArea rows={2} />
                </Form.Item>
                <Form.Item name="scope_of_work_text" label="Scope of Work">
                  <TextArea rows={2} />
                </Form.Item>

                <Typography.Title level={5} style={{ marginTop: 8 }}>Findings</Typography.Title>
                <Form.List name="findings_rows">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map((field, index) => (
                        <Row gutter={8} key={field.key} align="middle">
                          <Col xs={24} md={3}>
                            <Form.Item name={[field.name, 's_no']} label={index === 0 ? 'S-No' : ''}>
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={10}>
                            <Form.Item name={[field.name, 'finding']} label={index === 0 ? 'Finding' : ''}>
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={10}>
                            <Form.Item name={[field.name, 'action_to_be_taken']} label={index === 0 ? 'Action to be taken' : ''}>
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={1}>
                            <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                          </Col>
                        </Row>
                      ))}
                      <Button icon={<PlusOutlined />} onClick={() => add({ s_no: '', finding: '', action_to_be_taken: '' })}>
                        Add Finding Row
                      </Button>
                    </>
                  )}
                </Form.List>

                <Typography.Title level={5} style={{ marginTop: 16 }}>Parts to be Replaced</Typography.Title>
                <Form.List name="parts_replace_rows">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map((field, index) => (
                        <Row gutter={8} key={field.key} align="middle">
                          <Col xs={24} md={3}>
                            <Form.Item name={[field.name, 's_no']} label={index === 0 ? 'S-No' : ''}>
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={11}>
                            <Form.Item name={[field.name, 'part']} label={index === 0 ? 'Part' : ''}>
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={4}>
                            <Form.Item name={[field.name, 'qty']} label={index === 0 ? 'Qty' : ''}>
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={5}>
                            <Form.Item name={[field.name, 'uom']} label={index === 0 ? 'UOM' : ''}>
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={1}>
                            <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                          </Col>
                        </Row>
                      ))}
                      <Button icon={<PlusOutlined />} onClick={() => add({ s_no: '', part: '', qty: '', uom: '' })}>
                        Add Replaced Part Row
                      </Button>
                    </>
                  )}
                </Form.List>

                <Typography.Title level={5} style={{ marginTop: 16 }}>Parts to be Repaired / Machined</Typography.Title>
                <Form.List name="parts_repair_rows">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map((field, index) => (
                        <Row gutter={8} key={field.key} align="middle">
                          <Col xs={24} md={3}>
                            <Form.Item name={[field.name, 's_no']} label={index === 0 ? 'S-No' : ''}>
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={11}>
                            <Form.Item name={[field.name, 'part']} label={index === 0 ? 'Part' : ''}>
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={4}>
                            <Form.Item name={[field.name, 'qty']} label={index === 0 ? 'Qty' : ''}>
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={5}>
                            <Form.Item name={[field.name, 'uom']} label={index === 0 ? 'UOM' : ''}>
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={1}>
                            <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                          </Col>
                        </Row>
                      ))}
                      <Button icon={<PlusOutlined />} onClick={() => add({ s_no: '', part: '', qty: '', uom: '' })}>
                        Add Repair Row
                      </Button>
                    </>
                  )}
                </Form.List>

                <Form.Item name="remarks" label="Remarks" style={{ marginTop: 16 }}>
                  <TextArea rows={3} />
                </Form.Item>

                <Row gutter={12}>
                  <Col xs={24} md={12}>
                    <Form.Item name="prepared_by_print_name" label="Prepared By - Print Name">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="prepared_by_designation" label="Prepared By - Designation">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col xs={24} md={12}>
                    <Form.Item name="approved_by_print_name" label="Approved By - Print Name">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="approved_by_designation" label="Approved By - Designation">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Card>

            {/* Work Order Documents Section */}
            {selectedIIR.work_order_id && (
              <Card
                title={
                  <Space>
                    <span>Work Order Documents</span>
                    <Tag>{workOrder?.work_order_number || selectedIIR.work_order_id.substring(0, 8)}</Tag>
                  </Space>
                }
                extra={
                  <DocumentUpload
                    entityType="WorkOrder"
                    entityId={selectedIIR.work_order_id}
                    buttonText="Add Document"
                    onSuccess={() => {
                      // Documents will be refetched automatically by DocumentList
                      // Also refetch work order to get updated documents from relation
                      refetchWorkOrder();
                    }}
                  />
                }
              >
                <div style={{ marginBottom: 8 }}>
                  <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                    Work Order: {workOrder?.work_order_number || selectedIIR.work_order_id.substring(0, 8)}
                  </Typography.Text>
                </div>
                
                {/* Show work order documents from relation (work_order_documents table) */}
                {workOrder?.documents && workOrder.documents.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <Typography.Text strong style={{ fontSize: '14px', display: 'block', marginBottom: 8 }}>
                      Work Order Documents ({workOrder.documents.length})
                    </Typography.Text>
                    <Table
                      dataSource={workOrder.documents}
                      rowKey="id"
                      pagination={false}
                      size="small"
                      columns={[
                        {
                          title: 'Type',
                          dataIndex: 'document_type',
                          width: 100,
                          render: (type: string) => <Tag color="blue">{type}</Tag>,
                        },
                        {
                          title: 'Document Name',
                          dataIndex: 'document_name',
                        },
                        {
                          title: 'Uploaded',
                          dataIndex: 'created_at',
                          width: 150,
                          render: (date: string) => date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-',
                        },
                        {
                          title: 'Actions',
                          key: 'actions',
                          width: 150,
                          render: (_: any, record: any) => {
                            const canDelete = user?.id === record.uploaded_by;
                            const apiUrl = API_BASE_URL;
                            const fileUrl = `${apiUrl}/manufacturing/work-orders/${selectedIIR.work_order_id}/documents/${record.id}`;

                            const handleView = async () => {
                              try {
                                const token = localStorage.getItem('token');
                                const response = await fetch(fileUrl, {
                                  headers: {
                                    'Authorization': `Bearer ${token}`,
                                  },
                                });

                                if (!response.ok) {
                                  throw new Error(`HTTP error! status: ${response.status}`);
                                }

                                const blob = await response.blob();
                                const url = window.URL.createObjectURL(blob);
                                window.open(url, '_blank');
                                setTimeout(() => window.URL.revokeObjectURL(url), 100);
                              } catch (error) {
                                console.error('View error:', error);
                                messageApi.error('Failed to open document. Please check if the file exists.');
                              }
                            };

                            const handleDownload = async () => {
                              try {
                                const token = localStorage.getItem('token');
                                const response = await fetch(fileUrl, {
                                  headers: {
                                    'Authorization': `Bearer ${token}`,
                                  },
                                });

                                if (!response.ok) {
                                  throw new Error(`HTTP error! status: ${response.status}`);
                                }

                                const blob = await response.blob();
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = record.document_name;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(url);
                                messageApi.success('Document downloaded');
                              } catch (error) {
                                console.error('Download error:', error);
                                messageApi.error('Failed to download document. Please check if the file exists.');
                              }
                            };

                            const handleDelete = async () => {
                              try {
                                const { workOrderService } = await import('../../services/workOrder.service');
                                await workOrderService.deleteDocument(selectedIIR.work_order_id, record.id);
                                messageApi.success('Document deleted successfully');
                                refetchWorkOrder();
                              } catch (error: any) {
                                console.error('Delete error:', error);
                                messageApi.error(error.message || 'Failed to delete document');
                              }
                            };

                            return (
                              <Space>
                                <Tooltip title="View">
                                  <Button
                                    type="link"
                                    icon={<EyeOutlined />}
                                    onClick={handleView}
                                  />
                                </Tooltip>
                                <Tooltip title="Download">
                                  <Button
                                    type="link"
                                    icon={<DownloadOutlined />}
                                    onClick={handleDownload}
                                  />
                                </Tooltip>
                                {canDelete && (
                                  <Popconfirm
                                    title="Are you sure you want to delete this document?"
                                    onConfirm={handleDelete}
                                    okText="Yes"
                                    cancelText="No"
                                  >
                                    <Tooltip title="Delete">
                                      <Button
                                        type="link"
                                        danger
                                        icon={<DeleteOutlined />}
                                      />
                                    </Tooltip>
                                  </Popconfirm>
                                )}
                              </Space>
                            );
                          },
                        },
                      ]}
                    />
                  </div>
                )}
                
                {/* Show documents from main documents table (entity_type/entity_id) */}
                <div>
                  <Typography.Text strong style={{ fontSize: '14px', display: 'block', marginBottom: 8 }}>
                    Attached Documents
                  </Typography.Text>
                  <DocumentList
                    entityType="WorkOrder"
                    entityId={selectedIIR.work_order_id}
                  />
                </div>
              </Card>
            )}
          </Space>
        )}
      </Modal>

      {/* Reject Modal */}
      <Modal
        title="Reject IIR"
        open={isRejectModalVisible}
        onOk={() => rejectForm.submit()}
        onCancel={() => {
          setIsRejectModalVisible(false);
          rejectForm.resetFields();
        }}
        confirmLoading={rejectMutation.isPending}
      >
        <Form
          form={rejectForm}
          layout="vertical"
          onFinish={handleRejectSubmit}
        >
          <Form.Item
            name="rejectionReason"
            label="Rejection Reason"
            rules={[{ required: true, message: 'Please enter rejection reason' }]}
          >
            <TextArea
              rows={4}
              placeholder="Enter reason for rejection"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Assign Inspector Modal */}
      <Modal
        title="Assign Inspector"
        open={isAssignModalVisible}
        onOk={() => assignForm.submit()}
        onCancel={() => {
          setIsAssignModalVisible(false);
          assignForm.resetFields();
        }}
        okText="Assign"
        confirmLoading={assignMutation.isPending}
      >
        <Form
          form={assignForm}
          layout="vertical"
          onFinish={handleAssignSubmit}
          initialValues={{
            inspectorId: selectedIIR?.inspector_id,
          }}
        >
          <Form.Item
            name="inspectorId"
            label="Inspector"
            rules={[{ required: true, message: 'Please select an inspector' }]}
          >
            <Select
              showSearch
              placeholder="Select inspector"
              loading={isLoadingUsers}
              optionFilterProp="children"
              filterOption={(input, option) =>
                String(option?.label ?? option?.children ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {users.map((user) => (
                <Option key={user.id} value={user.id}>
                  {`${user.first_name} ${user.last_name || ''}`.trim() || user.email} {user.email && `(${user.email})`}
                </Option>
              ))}
            </Select>
          </Form.Item>
          {selectedIIR?.inspector_id && (
            <Typography.Text type="secondary">
              Current inspector: {displayUser(selectedIIR.inspector_id)}
            </Typography.Text>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default InitialInspectionRequestsPage;
